import { useCallback, useEffect, useState, useSyncExternalStore } from 'react'
import { ListBox, Select } from '@heroui/react'
import { api } from '../lib/api'
import {
  contextOrganisation,
  onOrganisationChange,
  organisationNeeded,
  setContextOrganisation,
  switchable,
  validChoice,
} from '../lib/ownerOrg'
import type { Identity, Organisation } from '../lib/types'
import { hasUnsaved } from '../lib/unsaved'
import { EmptyState } from './parts'
import { Icon } from './icons'
import { useStaffText } from './StaffText'
import { useSession } from './Session'

export interface OrgContext {
  /** The top bar offers a choice: the installation owner, several organisations. */
  switchable: boolean
  organisations: Organisation[]
  /** The chosen organisation's id; '' for none. */
  organisation: string
  /** Org-scoped pages have nothing to read until one is chosen. */
  needed: boolean
  choose: (id: string) => void
  reload: () => void
}

/** The owner's organisation context; inert for everybody else. */
export function useOrgContext(identity: Identity | null): OrgContext {
  const crosses = Boolean(identity?.permissions.includes('tenant.manage'))
  const member = Boolean(identity?.staff.tenantId)
  const chosen = useSyncExternalStore(onOrganisationChange, contextOrganisation, () => '')
  const [organisations, setOrganisations] = useState<Organisation[] | null>(null)
  const [asked, setAsked] = useState(0)
  const signedIn = identity?.staff.id

  useEffect(() => {
    if (!signedIn) return
    if (!crosses) {
      // Nobody else names an organisation; a previous owner's choice in this tab goes.
      setContextOrganisation('')
      setOrganisations(null)
      return
    }
    let live = true
    api
      .allOrganisations()
      .then(({ organisations: listed }) => {
        if (!live) return
        setOrganisations(listed)
        setContextOrganisation(validChoice(contextOrganisation(), listed))
      })
      .catch(() => live && setOrganisations([]))
    return () => {
      live = false
    }
  }, [signedIn, crosses, asked])

  const reload = useCallback(() => setAsked((n) => n + 1), [])
  const choice = { crosses, member, organisations, chosen }
  return {
    switchable: switchable(choice),
    organisations: organisations ?? [],
    organisation: crosses ? chosen : '',
    needed: Boolean(identity) && organisationNeeded(choice),
    choose: setContextOrganisation,
    reload,
  }
}

/** The organisation picker: in the top bar, and on a page waiting for a choice. */
export function OrganisationSelect({ inBar }: { inBar?: boolean }) {
  // Off the bar it asks for one organisation, so "all" is not offered.
  const { say } = useStaffText()
  const session = useSession()
  const org = session?.org
  if (!session || !org?.switchable) return null
  const own = session.identity.staff.tenantId ? (session.identity.organisation ?? say('admin.mine')) : say('common.allOrganisations')
  const options = [...(inBar ? [{ value: '', label: own }] : []), ...org.organisations.map((o) => ({ value: o.id, label: o.name }))]
  return (
    <Select
      aria-label={say('common.organisation')}
      variant="secondary"
      fullWidth={inBar}
      className={inBar ? undefined : 'org-pick'}
      placeholder={say('common.organisation')}
      value={org.organisation || (inBar ? ALL : null)}
      onChange={(key) => {
        if (key == null) return
        const next = key === ALL ? '' : String(key)
        if (next === org.organisation) return
        if (hasUnsaved() && !window.confirm(say('ui.discardAsk'))) return
        org.choose(next)
      }}
    >
      <Select.Trigger>
        <Select.Value />
        <Select.Indicator />
      </Select.Trigger>
      <Select.Popover>
        <ListBox>
          {options.map((o) => (
            <ListBox.Item key={o.value || ALL} id={o.value || ALL} textValue={o.label}>
              {o.label}
              <ListBox.ItemIndicator />
            </ListBox.Item>
          ))}
        </ListBox>
      </Select.Popover>
    </Select>
  )
}

// A list box key may not be empty: "every organisation" is '' in the context.
const ALL = '*all*'

/** One line and the picker, where an owner has chosen no organisation. */
export function OrganisationNeeded() {
  const { say } = useStaffText()
  const loaded = (useSession()?.org?.organisations.length ?? 0) > 0
  return (
    <EmptyState
      icon={<Icon name="organisations" size={20} />}
      title={say('people.chooseOrganisation')}
      action={loaded ? <OrganisationSelect /> : undefined}
    />
  )
}

/** An org-scoped page for an owner who has chosen no organisation. */
export function ChooseOrganisation({ title }: { title: string }) {
  return (
    <main className="page">
      <header className="page__head">
        <h1 className="page__title">{title}</h1>
      </header>
      <div className="table-card">
        <OrganisationNeeded />
      </div>
    </main>
  )
}
